/**
 * Work from home (19 Oct 2026, Jeremy's brief): its own tab, deliberately unlike the screens for
 * booking time off. One month, one click per day, and the click is the save: none, then a full
 * day, then half a day, then none again. No form, no approval and no notice, because nothing
 * here is decided by anybody.
 *
 * Days before the 1st of the current month are read-only, weekends and company holidays cannot
 * be marked, and the day is always the signed-in person's own (HR changes somebody else's only
 * through Act as). The API enforces all of it; this screen only draws it.
 */
import { mdy, WFH_PORTIONS, type PersonSummary, type WfhMonth, type WfhPortion } from "@scheduler/shared";
import { useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { localToday, monthLabel, shiftMonth } from "../calendar/TeamCalendar";
import { navigate } from "../router";

/** What one click does: none → full day → half day → none. */
function nextPortion(now: WfhPortion | undefined): WfhPortion | null {
  if (now === undefined) return WFH_PORTIONS[0]!;
  const i = WFH_PORTIONS.indexOf(now);
  return i + 1 < WFH_PORTIONS.length ? WFH_PORTIONS[i + 1]! : null;
}

export const WFH_TEXT: Record<WfhPortion, string> = { FULL: "WFH", HALF: "WFH ½" };
const SPOKEN: Record<WfhPortion, string> = { FULL: "working from home all day", HALF: "working from home half the day" };

export function WorkFromHome({ me, month: asked }: { me: PersonSummary; month: string | null }) {
  const api = useSchedulerApi();
  const today = localToday();
  const month = asked ?? today.slice(0, 7);
  const [data, setData] = useState<WfhMonth | null>(null);
  const [mine, setMine] = useState<Record<string, WfhPortion>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    let current = true;
    setError(null);
    api.wfhMonth(month)
      .then((m) => {
        if (!current) return;
        setData(m);
        setMine(Object.fromEntries(m.entries.filter((e) => e.person.id === me.id).map((e) => [e.date, e.portion])));
      })
      .catch((e: unknown) => { if (current) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { current = false; };
  }, [api, month, me.id, back]);

  const go = (next: string | null) => navigate({ kind: "wfh", month: next });

  const click = async (date: string): Promise<void> => {
    const next = nextPortion(mine[date]);
    setSaving(date);
    setError(null);
    try {
      if (next) await api.wfhSet(date, next);
      else await api.wfhClear(date);
      setMine((m) => {
        const out = { ...m };
        if (next) out[date] = next;
        else delete out[date];
        return out;
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  // Sunday-first weeks, padded with blank cells so the 1st falls under the right weekday.
  const lead = data?.days[0]?.weekday ?? 0;
  const cells = data ? [...Array.from<null>({ length: lead }).fill(null), ...data.days] : [];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <section className="wfh" aria-label="Work from home">
      <p className="wfh-help">Click a day to mark it as working from home.</p>
      <div className="wfh-nav">
        <button type="button" className="ghost" onClick={() => go(shiftMonth(month, -1))}>← Prev</button>
        <h2>{monthLabel(month)}</h2>
        <button type="button" className="ghost" onClick={() => go(shiftMonth(month, 1))}>Next →</button>
        <button type="button" className="ghost" disabled={month === today.slice(0, 7)} onClick={() => go(null)}>Today</button>
      </div>
      {error ? <div className="error" role="alert">{error}</div> : null}
      {data === null && !error ? <p className="lede">Loading…</p> : null}
      {data ? (
        <div className="wfh-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="wfh-dow">{d}</div>)}
          {cells.map((day, i) => {
            if (!day) return <div key={`pad-${i}`} className="wfh-day pad" />;
            const portion = mine[day.date];
            const closed = day.weekend || day.holiday !== null;
            const past = day.date < data.editableFrom;
            const cls = `wfh-day${closed ? " closed" : ""}${past ? " past" : ""}${portion ? ` on ${portion.toLowerCase()}` : ""}${day.date === today ? " today" : ""}`;
            const inner = (
              <>
                <span className="wfh-n">{day.day}</span>
                {day.holiday ? <span className="wfh-hol">{day.holiday}</span> : null}
                {portion ? <span className="wfh-mark">{WFH_TEXT[portion]}</span> : null}
              </>
            );
            if (closed || past) {
              return (
                <div key={day.date} className={cls} title={day.holiday ?? (day.weekend ? "Weekend" : "Earlier months cannot be changed")}>
                  {inner}
                </div>
              );
            }
            return (
              <button
                key={day.date} type="button" className={cls} disabled={saving === day.date}
                aria-label={`${mdy(day.date)}: ${portion ? SPOKEN[portion] : "not working from home"}`}
                onClick={() => void click(day.date)}
              >
                {inner}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
